import { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Wrench, Pencil, Trash2, History } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTaxonomy, SECTOR_COLORS } from "@/lib/taxonomy";
import { TaxonomyManager } from "@/components/TaxonomyManager";

const EMPTY = { tag: "", name: "", sector: "", description: "" };

export default function Equipments() {
  const { user } = useAuth();
  const isSupervisor = ["supervisor", "almoxarife"].includes(user?.role);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = criar, id = editar
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const { sectors, disciplines, reload: reloadTax } = useTaxonomy();
  const load = useCallback(() => api.get("/equipments").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const reloadAll = () => { load(); reloadTax(); };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY, sector: sectors[0]?.name || "" });
    setOpen(true);
  };

  const openEdit = (eq) => {
    setEditingId(eq.id);
    setForm({ tag: eq.tag, name: eq.name, sector: eq.sector, description: eq.description || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.tag.trim() || !form.name.trim() || !form.sector) {
      toast.error("TAG, Nome e Setor são obrigatórios");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/equipments/${editingId}`, form);
        toast.success("Equipamento atualizado");
      } else {
        await api.post("/equipments", form);
        toast.success("Equipamento cadastrado");
      }
      setOpen(false);
      setForm(EMPTY);
      setEditingId(null);
      reloadAll();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (eq) => {
    try {
      const { data } = await api.delete(`/equipments/${eq.id}`);
      const preserved = data.linked_os_preserved || 0;
      toast.success(preserved > 0
        ? `Equipamento excluído. ${preserved} OS histórica(s) preservada(s).`
        : "Equipamento excluído."
      );
      reloadAll();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const sectorNames = new Set(sectors.map((s) => s.name));
  const groups = sectors.map((s, i) => ({ title: s.name, data: items.filter((e) => e.sector === s.name), color: SECTOR_COLORS[i % SECTOR_COLORS.length] }));
  const orphans = items.filter((e) => !sectorNames.has(e.sector));
  if (orphans.length) groups.push({ title: "Sem setor cadastrado", data: orphans, color: "bg-slate-500" });

  return (
    <div className="space-y-6" data-testid="equipments">
      <div className="flex justify-between items-center gap-4">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">Cadastro</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Equipamentos</h1>
        </div>
        {isSupervisor && (
          <Button onClick={openCreate} className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white gap-2" data-testid="new-equipment-btn">
            <Plus className="w-4 h-4" /> Novo Equipamento
          </Button>
        )}
      </div>

      <Tabs defaultValue="equipments">
        <TabsList className="rounded-sm">
          <TabsTrigger value="equipments" className="rounded-sm" data-testid="tab-equipments">Equipamentos ({items.length})</TabsTrigger>
          {isSupervisor && <TabsTrigger value="sectors" className="rounded-sm" data-testid="tab-sectors">Setores ({sectors.length})</TabsTrigger>}
          {isSupervisor && <TabsTrigger value="disciplines" className="rounded-sm" data-testid="tab-disciplines">Disciplinas ({disciplines.length})</TabsTrigger>}
        </TabsList>
        {isSupervisor && (
          <TabsContent value="sectors" className="mt-4">
            <TaxonomyManager title="Setores" label="Setor" slug="sector" endpoint="sectors" items={sectors} onChange={reloadAll} />
          </TabsContent>
        )}
        {isSupervisor && (
          <TabsContent value="disciplines" className="mt-4">
            <TaxonomyManager title="Disciplinas" label="Disciplina" slug="discipline" endpoint="disciplines" items={disciplines} onChange={reloadAll} />
          </TabsContent>
        )}
        <TabsContent value="equipments" className="mt-4 space-y-6">
      {groups.map((sec) => (
        <Card key={sec.title} className="rounded-sm shadow-none" data-testid={`sector-group-${sec.title}`}>
          <div className="p-5 border-b flex items-center gap-3">
            <div className={`w-8 h-8 rounded-sm ${sec.color} flex items-center justify-center`}>
              <Wrench className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">Setor</div>
              <h3 className="font-heading text-xl font-bold">{sec.title}</h3>
            </div>
            <Badge variant="outline" className="rounded-sm ml-auto">{sec.data.length} equipamentos</Badge>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">TAG</TableHead>
                <TableHead>Equipamento</TableHead>
                <TableHead className="hidden md:table-cell">Descrição</TableHead>
                <TableHead className="w-24 text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sec.data.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="font-mono-tag font-semibold text-orange-600">{e.tag}</TableCell>
                  <TableCell className="font-medium">{e.name}</TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{e.description}</TableCell>
                    <TableCell className="text-right space-x-2 whitespace-nowrap">
                      <Button onClick={() => navigate(`/equipamentos/${e.id}`)} variant="outline" size="sm" className="rounded-sm gap-1.5 h-8" data-testid={`history-equipment-${e.tag}`}>
                        <History className="w-3.5 h-3.5" /> Histórico
                      </Button>
                  {isSupervisor && (<>
                      <Button
                        onClick={() => openEdit(e)}
                        variant="outline"
                        size="sm"
                        className="rounded-sm gap-1.5 h-8"
                        data-testid={`edit-equipment-${e.tag}`}
                      >
                        <Pencil className="w-3.5 h-3.5" /> Editar
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-sm gap-1.5 h-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-500/10"
                            data-testid={`delete-equipment-${e.tag}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent data-testid="delete-equipment-dialog">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir {e.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              <span className="block mb-2">Isso removerá o equipamento <span className="font-mono-tag text-orange-600 font-semibold">{e.tag}</span> do cadastro e apagará seus agendamentos de preventiva.</span>
                              <span className="block text-xs">As Ordens de Serviço históricas serão <b>preservadas</b> com o nome e TAG atuais para não perder o histórico. Essa ação não pode ser desfeita.</span>
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => remove(e)}
                              className="bg-red-600 hover:bg-red-700"
                              data-testid={`confirm-delete-${e.tag}`}
                            >
                              Excluir Equipamento
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                  </>)}
                    </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ))}
        </TabsContent>
      </Tabs>

      {/* Dialog compartilhado: criar / editar */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(EMPTY); } }}>
        <DialogContent data-testid="equipment-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Equipamento" : "Novo Equipamento"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>TAG *</Label>
              <Input
                value={form.tag}
                onChange={(e) => setForm({ ...form, tag: e.target.value.toUpperCase() })}
                placeholder="Ex: BM-02"
                className="rounded-sm font-mono-tag mt-1.5"
                data-testid="equipment-tag-input"
              />
            </div>
            <div>
              <Label>Nome *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-sm mt-1.5"
                data-testid="equipment-name-input"
              />
            </div>
            <div>
              <Label>Setor *</Label>
              <Select value={form.sector} onValueChange={(v) => setForm({ ...form, sector: v })}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="equipment-sector-input"><SelectValue placeholder="Selecione o setor" /></SelectTrigger>
                <SelectContent>
                  {sectors.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="rounded-sm mt-1.5"
                rows={2}
              />
            </div>
            {editingId && (
              <p className="text-xs text-muted-foreground border-l-2 border-orange-500 pl-3 py-1">
                Ao alterar a TAG ou nome, todas as Ordens de Serviço vinculadas serão atualizadas automaticamente.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={save} disabled={saving} className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white" data-testid="equipment-save-btn">
              {saving ? "Salvando..." : editingId ? "Salvar Alterações" : "Cadastrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
